import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
    Shield, Bell, Settings, LogOut, Monitor, Server, Smartphone, Cloud,
    AlertTriangle, CheckCircle, Clock, Eye, Brain, Scale, Activity,
    ChevronRight, RefreshCw, Volume2, VolumeX, Laptop, Menu, X, Download,
    Cpu, Wifi, WifiOff, Terminal, Ban, FileX, Power, Play, Square, Key
} from 'lucide-react';
import { dashboardAPI, devicesAPI, threatsAPI, auditAPI, voiceAPI, agentsAPI, analysisAPI, mitreAPI } from '@/lib/api';
import { LOGO_URL } from '@/lib/brand';

const Dashboard = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [stats, setStats] = useState(null);
    const [devices, setDevices] = useState([]);
    const [threats, setThreats] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [agents, setAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [voiceEnabled, setVoiceEnabled] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [commandLoading, setCommandLoading] = useState(false);
    // Scan-related state
    const [scanningAgents, setScanningAgents] = useState({});
    const [activeScan, setActiveScan] = useState(null);
    const [scanResultsModal, setScanResultsModal] = useState(null);
    const [pollInterval, setPollInterval] = useState(null);
    // AI Analysis state
    const [aiAnalyzing, setAiAnalyzing] = useState(false);
    const [aiAnalysisResult, setAiAnalysisResult] = useState(null);

    useEffect(() => {
        const userData = localStorage.getItem('shirosec_user');
        if (!userData) {
            navigate('/login');
            return;
        }
        setUser(JSON.parse(userData));
        loadDashboardData();
    }, [navigate]);

    const loadDashboardData = async () => {
        setIsLoading(true);
        try {
            const [statsRes, devicesRes, threatsRes, logsRes, agentsRes] = await Promise.all([
                dashboardAPI.getStats(),
                devicesAPI.getAll(),
                threatsAPI.getAll(),
                auditAPI.getLogs(20),
                agentsAPI.list().catch(() => ({ data: [] }))
            ]);
            setStats(statsRes.data);
            setDevices(devicesRes.data);
            setThreats(threatsRes.data);
            setAuditLogs(logsRes.data);
            setAgents(agentsRes.data || []);
        } catch (error) {
            console.error('Error loading dashboard:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('shirosec_token');
        localStorage.removeItem('shirosec_user');
        navigate('/login');
    };

    const handleResolve = async (threatId) => {
        try {
            await threatsAPI.resolve(threatId);
            loadDashboardData();
        } catch (error) {
            console.error('Error resolving threat:', error);
        }
    };

    const handleSendCommand = async (agentId, commandType, params = {}) => {
        setCommandLoading(true);
        try {
            await agentsAPI.sendCommand(agentId, commandType, params);
            toast.success(`${commandType === 'scan' ? 'Scan' : 'Command'} initiated successfully!`, {
                description: commandType === 'scan' 
                    ? 'Security scan is now running on the device.' 
                    : `Command "${commandType}" has been sent to the agent.`
            });
            loadDashboardData();
        } catch (error) {
            console.error('Error sending command:', error);
            toast.error('Failed to send command', {
                description: 'Please check if the agent is online and try again.'
            });
        } finally {
            setCommandLoading(false);
        }
    };

    // Start a security scan on an agent
    const handleStartScan = async (agentId) => {
        try {
            // Mark agent as scanning in local state
            setScanningAgents(prev => ({ ...prev, [agentId]: { status: 'starting', progress: 0 } }));
            
            const response = await agentsAPI.startScan(agentId, 'quick');
            
            if (response.data.status === 'already_running') {
                toast.info('Scan already in progress', {
                    description: 'A security scan is already running on this device.'
                });
                // Start polling for existing scan
                pollScanStatus(response.data.scan_id, agentId);
            } else if (response.data.status === 'started') {
                toast.success('Security scan started!', {
                    description: 'Scanning for threats, vulnerabilities, and suspicious activity...'
                });
                setScanningAgents(prev => ({ 
                    ...prev, 
                    [agentId]: { status: 'running', progress: 0, scanId: response.data.scan_id } 
                }));
                // Start polling for scan status
                pollScanStatus(response.data.scan_id, agentId);
            }
        } catch (error) {
            console.error('Error starting scan:', error);
            setScanningAgents(prev => {
                const newState = { ...prev };
                delete newState[agentId];
                return newState;
            });
            toast.error('Failed to start scan', {
                description: error.response?.data?.detail || 'Please check if the agent is online.'
            });
        }
    };

    // Poll scan status
    const pollScanStatus = (scanId, agentId) => {
        // Clear any existing interval
        if (pollInterval) {
            clearInterval(pollInterval);
        }
        
        const interval = setInterval(async () => {
            try {
                const response = await agentsAPI.getScanStatus(scanId);
                const scan = response.data;
                
                setScanningAgents(prev => ({
                    ...prev,
                    [agentId]: {
                        status: scan.status,
                        progress: scan.progress,
                        phase: scan.current_phase,
                        scanId: scanId,
                        findings: scan.findings?.length || 0
                    }
                }));
                
                // If scan is complete or failed, stop polling
                if (scan.status === 'completed' || scan.status === 'failed') {
                    clearInterval(interval);
                    setPollInterval(null);
                    
                    if (scan.status === 'completed') {
                        const threatCount = scan.findings?.filter(f => f.severity === 'high' || f.severity === 'critical').length || 0;
                        toast.success('Scan completed!', {
                            description: threatCount > 0 
                                ? `Found ${scan.findings?.length || 0} items (${threatCount} high/critical)`
                                : `Scanned ${scan.total_items_scanned || 0} items. No critical threats found.`,
                            action: {
                                label: 'View Results',
                                onClick: () => setScanResultsModal(scan)
                            }
                        });
                    } else {
                        toast.error('Scan failed', {
                            description: scan.error || 'An error occurred during the scan.'
                        });
                    }
                    
                    // Remove from scanning state after a delay
                    setTimeout(() => {
                        setScanningAgents(prev => {
                            const newState = { ...prev };
                            delete newState[agentId];
                            return newState;
                        });
                    }, 3000);
                    
                    // Refresh dashboard data
                    loadDashboardData();
                }
            } catch (error) {
                console.error('Error polling scan status:', error);
            }
        }, 2000); // Poll every 2 seconds
        
        setPollInterval(interval);
    };

    // View scan results for an agent
    const handleViewScanResults = async (agentId) => {
        try {
            const response = await agentsAPI.getLatestScan(agentId);
            if (response.data.status === 'no_scans') {
                toast.info('No scan history', {
                    description: 'No scans have been run on this device yet.'
                });
            } else {
                setScanResultsModal(response.data);
            }
        } catch (error) {
            console.error('Error fetching scan results:', error);
            toast.error('Failed to load scan results');
        }
    };

    // AI Analysis of scan results
    const handleAIAnalysis = async (scanId) => {
        setAiAnalyzing(true);
        try {
            const response = await analysisAPI.analyzeScan(scanId);
            if (response.data) {
                // Ensure all required fields have defaults
                const result = {
                    scan_id: scanId,
                    severity_assessment: response.data.severity_assessment || 'medium',
                    risk_score: response.data.risk_score || 50,
                    summary: response.data.summary || 'Analysis complete.',
                    recommended_actions: response.data.recommended_actions || [],
                    ai_powered: response.data.ai_powered || false,
                    ...response.data
                };
                setAiAnalysisResult(result);
                toast.success('AI Analysis Complete!', {
                    description: `Risk Score: ${result.risk_score}/100`
                });
            } else {
                throw new Error('No data received');
            }
        } catch (error) {
            console.error('Error analyzing scan:', error);
            toast.error('AI Analysis failed', {
                description: error.message || 'Please try again.'
            });
            setAiAnalysisResult(null);
        } finally {
            setAiAnalyzing(false);
        }
    };

    // Create threats from scan findings
    const handleCreateThreatsFromScan = async (scanId) => {
        try {
            const response = await analysisAPI.createThreatsFromScan(scanId, 'high');
            toast.success(`Created ${response.data.threats_created} threats`, {
                description: 'High severity findings have been added to your threat list.'
            });
            loadDashboardData();
        } catch (error) {
            console.error('Error creating threats:', error);
            toast.error('Failed to create threats from scan');
        }
    };

    // Map findings to MITRE ATT&CK
    const handleMitreMapping = async (findings) => {
        try {
            const response = await mitreAPI.mapFindings(findings);
            toast.success('MITRE ATT&CK Mapping Complete', {
                description: `Overall Risk Score: ${response.data.overall_risk_score}/100`
            });
            return response.data;
        } catch (error) {
            console.error('Error mapping to MITRE:', error);
            toast.error('Failed to map to MITRE ATT&CK');
        }
    };

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [pollInterval]);

    const getAgentStatusColor = (status) => {
        switch (status) {
            case 'online': return 'bg-green-500';
            case 'offline': return 'bg-gray-500';
            case 'degraded': return 'bg-yellow-500';
            default: return 'bg-gray-500';
        }
    };

    const getTimeSince = (dateString) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        const now = new Date();
        const diff = Math.floor((now - date) / 1000);
        if (diff < 60) return `${diff}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    };

    const speakAlert = async (text) => {
        if (voiceEnabled && 'speechSynthesis' in window) {
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.rate = 1;
            utterance.pitch = 1;
            window.speechSynthesis.speak(utterance);
        }
    };

    const getDeviceIcon = (type) => {
        switch (type) {
            case 'laptop': return Laptop;
            case 'desktop': return Monitor;
            case 'server': return Server;
            case 'mobile': return Smartphone;
            case 'cloud_service': return Cloud;
            default: return Monitor;
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
            case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
            case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
            case 'low': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
            default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'online': return 'bg-green-400';
            case 'offline': return 'bg-gray-400';
            case 'warning': return 'bg-yellow-400';
            case 'critical': return 'bg-red-400';
            default: return 'bg-gray-400';
        }
    };

    const getAgentIcon = (layer) => {
        switch (layer) {
            case 'watchdog': return Eye;
            case 'investigator': return Brain;
            case 'jurist': return Scale;
            default: return Activity;
        }
    };

    const getAgentColor = (layer) => {
        switch (layer) {
            case 'watchdog': return 'text-cyan-400 bg-cyan-500/20';
            case 'investigator': return 'text-purple-400 bg-purple-500/20';
            case 'jurist': return 'text-green-400 bg-green-500/20';
            default: return 'text-gray-400 bg-gray-500/20';
        }
    };

    const circumference = 2 * Math.PI * 52;
    const scoreOffset = circumference - (circumference * (stats?.security_score || 0)) / 100;

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
                <div className="text-center">
                    <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
                    <p className="text-white/60">Loading security dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0f] text-white">
            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`fixed top-0 left-0 h-screen w-64 bg-[#0d0d14] border-r border-white/5 z-50 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} flex flex-col`}>
                <div className="p-6 flex-shrink-0">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Shiro Security" className="w-10 h-10 object-contain" />
                        <span className="text-xl font-bold gradient-text">SHIRO</span>
                    </Link>
                </div>
                
                <nav className="px-4 space-y-2 flex-1 overflow-y-auto pb-4">
                    {[
                        { id: 'overview', icon: Activity, label: 'Overview' },
                        { id: 'agents', icon: Cpu, label: 'Agents' },
                        { id: 'devices', icon: Monitor, label: 'Devices' },
                        { id: 'threats', icon: AlertTriangle, label: 'Threats' },
                        { id: 'audit', icon: Clock, label: 'Audit Logs' },
                    ].map((item) => (
                        <button
                            key={item.id}
                            onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                                activeTab === item.id
                                    ? 'bg-cyan-500/10 text-cyan-400'
                                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                            }`}
                            data-testid={`nav-${item.id}`}
                        >
                            <item.icon className="w-5 h-5" />
                            {item.label}
                            {item.id === 'agents' && agents.length > 0 && (
                                <Badge className="ml-auto bg-cyan-500/20 text-cyan-400 text-xs">{agents.length}</Badge>
                            )}
                        </button>
                    ))}
                    
                    {/* Download Agent Button */}
                    <Link
                        to="/agent/download"
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors bg-gradient-to-r from-cyan-500/10 to-purple-500/10 text-cyan-400 hover:from-cyan-500/20 hover:to-purple-500/20 border border-cyan-500/20"
                    >
                        <Download className="w-5 h-5" />
                        Download Agent
                    </Link>
                    
                    <div className="border-t border-white/5 my-4"></div>
                    
                    {/* Profile & Subscription Links */}
                    <Link
                        to="/profile"
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-white/60 hover:bg-white/5 hover:text-white"
                    >
                        <Settings className="w-5 h-5" />
                        Profile Settings
                    </Link>
                    <Link
                        to="/subscription"
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-white/60 hover:bg-white/5 hover:text-white"
                    >
                        <Shield className="w-5 h-5" />
                        Subscription
                    </Link>
                    <Link
                        to="/settings"
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-white/60 hover:bg-white/5 hover:text-white"
                        data-testid="nav-settings"
                    >
                        <Key className="w-5 h-5" />
                        API & Schedules
                    </Link>
                    <Link
                        to="/intelligence"
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors bg-gradient-to-r from-red-500/10 to-purple-500/10 text-red-400 hover:from-red-500/20 hover:to-purple-500/20 border border-red-500/20"
                        data-testid="nav-intelligence"
                    >
                        <Brain className="w-5 h-5" />
                        Threat Intelligence
                    </Link>
                </nav>

                <div className="flex-shrink-0 p-4 border-t border-white/5">
                    <div className="flex items-center gap-3 mb-3">
                        <Link to="/profile" className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-purple-600 flex items-center justify-center text-white font-bold hover:ring-2 hover:ring-cyan-500/50 transition-all flex-shrink-0">
                            {user?.full_name?.charAt(0) || 'U'}
                        </Link>
                        <div className="flex-1 min-w-0">
                            <Link to="/profile" className="text-sm font-medium truncate hover:text-cyan-400 transition-colors block">{user?.full_name}</Link>
                            <div className="text-xs text-white/40 truncate">{user?.company_name}</div>
                        </div>
                    </div>
                    {/* Prominent Logout Button */}
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 hover:text-red-300"
                        onClick={handleLogout}
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                    </Button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="lg:ml-64 min-h-screen">
                {/* Top Bar */}
                <header className="sticky top-0 z-30 bg-[#0a0a0f]/80 backdrop-blur-lg border-b border-white/5 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button 
                                className="lg:hidden p-2 text-white/60 hover:text-white"
                                onClick={() => setSidebarOpen(true)}
                            >
                                <Menu className="w-6 h-6" />
                            </button>
                            <h1 className="text-xl font-bold capitalize">{activeTab}</h1>
                        </div>
                        <div className="flex items-center gap-4">
                            <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={loadDashboardData}
                                className="text-white/60 hover:text-white hover:bg-white/5"
                                data-testid="refresh-btn"
                            >
                                <RefreshCw className="w-5 h-5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-white/60 hover:text-white hover:bg-white/5 relative">
                                <Bell className="w-5 h-5" />
                                {stats?.threats_investigating > 0 && (
                                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                                        {stats.threats_investigating}
                                    </span>
                                )}
                            </Button>
                        </div>
                    </div>
                </header>

                <div className="p-6">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {/* Demo Data Banner - Only show if no agents */}
                            {agents.length === 0 && (
                            <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-xl p-4 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                                    <Eye className="w-5 h-5 text-cyan-400" />
                                </div>
                                <div className="flex-1">
                                    <div className="font-semibold text-white">Demo Data Preview</div>
                                    <div className="text-sm text-white/60">This dashboard shows sample data. Deploy the agent on your devices to see real security data.</div>
                                </div>
                                <Link to="/agent/download">
                                    <Button size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-white">
                                        <Download className="w-4 h-4 mr-2" /> Get Agent
                                    </Button>
                                </Link>
                            </div>
                            )}

                            {/* Stats Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <Card className="stat-card rounded-xl" data-testid="stat-devices">
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <Monitor className="w-8 h-8 text-cyan-400" />
                                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                                {stats?.online_devices} online
                                            </Badge>
                                        </div>
                                        <div className="text-3xl font-bold mb-1">{stats?.total_devices}</div>
                                        <div className="text-sm text-white/60">Protected Devices</div>
                                    </CardContent>
                                </Card>

                                <Card className="stat-card rounded-xl" data-testid="stat-threats">
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <AlertTriangle className="w-8 h-8 text-orange-400" />
                                            {stats?.threats_investigating > 0 && (
                                                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 animate-pulse">
                                                    {stats.threats_investigating} active
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="text-3xl font-bold mb-1">{stats?.total_threats}</div>
                                        <div className="text-sm text-white/60">Total Threats Detected</div>
                                    </CardContent>
                                </Card>

                                <Card className="stat-card rounded-xl" data-testid="stat-blocked">
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <CheckCircle className="w-8 h-8 text-green-400" />
                                        </div>
                                        <div className="text-3xl font-bold mb-1">{stats?.threats_blocked}</div>
                                        <div className="text-sm text-white/60">Threats Blocked</div>
                                    </CardContent>
                                </Card>

                                <Card className="stat-card rounded-xl" data-testid="stat-score">
                                    <CardContent className="p-6">
                                        <div className="flex items-center gap-4">
                                            <div className="security-score-ring">
                                                <svg width="120" height="120">
                                                    <defs>
                                                        <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                                            <stop offset="0%" stopColor="#00d4ff" />
                                                            <stop offset="100%" stopColor="#8b5cf6" />
                                                        </linearGradient>
                                                    </defs>
                                                    <circle className="bg" cx="60" cy="60" r="52" />
                                                    <circle 
                                                        className="progress" 
                                                        cx="60" 
                                                        cy="60" 
                                                        r="52"
                                                        strokeDasharray={circumference}
                                                        strokeDashoffset={scoreOffset}
                                                    />
                                                </svg>
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <span className="text-2xl font-bold">{stats?.security_score}%</span>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-sm text-white/60">Security Score</div>
                                                <div className="text-green-400 text-sm font-medium">Protected</div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* AI Layers Status */}
                            <Card className="glass-card border-0">
                                <CardHeader>
                                    <CardTitle>AI Security Layers</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid md:grid-cols-3 gap-4">
                                        {[
                                            { layer: 'watchdog', name: 'Watchdog', status: 'Active', desc: 'Scanning for anomalies' },
                                            { layer: 'investigator', name: 'Investigator', status: 'Ready', desc: 'Awaiting threats' },
                                            { layer: 'jurist', name: 'Jurist', status: 'Online', desc: 'Compliance verified' }
                                        ].map((ai) => {
                                            const Icon = getAgentIcon(ai.layer);
                                            return (
                                                <div key={ai.layer} className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getAgentColor(ai.layer)}`}>
                                                        <Icon className="w-6 h-6" />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">{ai.name}</div>
                                                        <div className="text-xs text-white/60">{ai.desc}</div>
                                                    </div>
                                                    <div className="ml-auto">
                                                        <span className="w-2 h-2 rounded-full bg-green-400 inline-block animate-pulse"></span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Recent Activity */}
                            <div className="grid lg:grid-cols-2 gap-6">
                                <Card className="glass-card border-0">
                                    <CardHeader className="flex flex-row items-center justify-between">
                                        <CardTitle>Recent Threats</CardTitle>
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => setActiveTab('threats')}
                                            className="text-cyan-400 hover:text-cyan-300"
                                        >
                                            View All <ChevronRight className="w-4 h-4 ml-1" />
                                        </Button>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            {stats?.recent_threats?.slice(0, 3).map((threat) => (
                                                <div key={threat.id} className="threat-card flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getSeverityColor(threat.severity)}`}>
                                                        <AlertTriangle className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium truncate">{threat.title}</div>
                                                        <div className="text-xs text-white/60">{new Date(threat.detected_at).toLocaleString()}</div>
                                                    </div>
                                                    <Badge className={getSeverityColor(threat.severity)}>
                                                        {threat.severity}
                                                    </Badge>
                                                </div>
                                            ))}
                                            {(!stats?.recent_threats || stats.recent_threats.length === 0) && (
                                                <div className="text-center text-white/40 py-8">
                                                    No recent threats detected
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="glass-card border-0">
                                    <CardHeader className="flex flex-row items-center justify-between">
                                        <CardTitle>AI Activity Log</CardTitle>
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => setActiveTab('audit')}
                                            className="text-cyan-400 hover:text-cyan-300"
                                        >
                                            View All <ChevronRight className="w-4 h-4 ml-1" />
                                        </Button>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            {stats?.recent_activities?.slice(0, 3).map((log) => {
                                                const Icon = getAgentIcon(log.agent_layer);
                                                return (
                                                    <div key={log.id} className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getAgentColor(log.agent_layer)}`}>
                                                            <Icon className="w-5 h-5" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-medium text-sm">{log.action}</div>
                                                            <div className="text-xs text-white/60 truncate">{log.reasoning}</div>
                                                        </div>
                                                        <Badge variant="outline" className="text-white/60 border-white/20">
                                                            {log.decision}
                                                        </Badge>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    )}

                    {/* Devices Tab */}
                    {activeTab === 'devices' && (
                        <div className="space-y-6">
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {devices.map((device) => {
                                    const DeviceIcon = getDeviceIcon(device.device_type);
                                    return (
                                        <Card key={device.id} className="glass-card border-0" data-testid={`device-${device.id}`}>
                                            <CardContent className="p-6">
                                                <div className="flex items-center gap-4 mb-4">
                                                    <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                                                        <DeviceIcon className="w-6 h-6 text-cyan-400" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="font-medium">{device.name}</div>
                                                        <div className="text-xs text-white/60">{device.os}</div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`w-2 h-2 rounded-full ${getStatusColor(device.status)}`}></span>
                                                        <span className="text-xs text-white/60 capitalize">{device.status}</span>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4 text-sm">
                                                    <div>
                                                        <div className="text-white/40">IP Address</div>
                                                        <div className="font-mono">{device.ip_address || 'N/A'}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-white/40">Threats Blocked</div>
                                                        <div className="text-green-400 font-bold">{device.threats_blocked}</div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Agents Tab */}
                    {activeTab === 'agents' && (
                        <div className="space-y-6">
                            {/* Agents Overview */}
                            <div className="grid md:grid-cols-3 gap-4">
                                <Card className="glass-card border-0">
                                    <CardContent className="p-4 text-center">
                                        <Cpu className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                                        <div className="text-2xl font-bold">{agents.length}</div>
                                        <div className="text-sm text-white/60">Total Agents</div>
                                    </CardContent>
                                </Card>
                                <Card className="glass-card border-0">
                                    <CardContent className="p-4 text-center">
                                        <Wifi className="w-8 h-8 text-green-400 mx-auto mb-2" />
                                        <div className="text-2xl font-bold">{agents.filter(a => a.status === 'online').length}</div>
                                        <div className="text-sm text-white/60">Online</div>
                                    </CardContent>
                                </Card>
                                <Card className="glass-card border-0">
                                    <CardContent className="p-4 text-center">
                                        <WifiOff className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                        <div className="text-2xl font-bold">{agents.filter(a => a.status !== 'online').length}</div>
                                        <div className="text-sm text-white/60">Offline</div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Agents List */}
                            {agents.length > 0 ? (
                                <div className="space-y-4">
                                    {agents.map((agent) => (
                                        <Card key={agent.agent_id} className="glass-card border-0">
                                            <CardContent className="p-6">
                                                <div className="flex items-start gap-4">
                                                    <div className="w-14 h-14 rounded-xl overflow-hidden">
                                                        <img src="/shiro-agent-logo.jpg" alt="Shiro Agent" className="w-full h-full object-cover" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <h3 className="font-bold text-lg">{agent.hostname}</h3>
                                                            <Badge className={`${agent.status === 'online' ? 'bg-green-500/20 text-green-400' : agent.status === 'degraded' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                                <span className={`w-2 h-2 rounded-full mr-2 inline-block ${getAgentStatusColor(agent.status)}`}></span>
                                                                {agent.status}
                                                            </Badge>
                                                            <Badge variant="outline" className="text-white/60 border-white/20">
                                                                v{agent.version}
                                                            </Badge>
                                                        </div>
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                                                            <div>
                                                                <div className="text-white/40">Operating System</div>
                                                                <div className="text-white/80">{agent.os}</div>
                                                            </div>
                                                            <div>
                                                                <div className="text-white/40">Architecture</div>
                                                                <div className="text-white/80">{agent.arch}</div>
                                                            </div>
                                                            <div>
                                                                <div className="text-white/40">IP Address</div>
                                                                <div className="text-white/80 font-mono">{agent.ip_addresses?.[0] || 'N/A'}</div>
                                                            </div>
                                                            <div>
                                                                <div className="text-white/40">Last Seen</div>
                                                                <div className="text-white/80">{getTimeSince(agent.last_seen)}</div>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Agent Commands */}
                                                        <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10">
                                                            <span className="text-xs text-white/40 mr-2 self-center">Commands:</span>
                                                            
                                                            {/* Scan Button with Progress */}
                                                            {scanningAgents[agent.agent_id] ? (
                                                                <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-md">
                                                                    <RefreshCw className="w-3 h-3 text-cyan-400 animate-spin" />
                                                                    <div className="flex flex-col">
                                                                        <span className="text-xs text-cyan-400 font-medium">
                                                                            {scanningAgents[agent.agent_id].phase || 'Scanning'}... {scanningAgents[agent.agent_id].progress}%
                                                                        </span>
                                                                        <div className="w-20 h-1 bg-cyan-500/20 rounded-full overflow-hidden">
                                                                            <div 
                                                                                className="h-full bg-cyan-400 transition-all duration-500"
                                                                                style={{ width: `${scanningAgents[agent.agent_id].progress}%` }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    {scanningAgents[agent.agent_id].findings > 0 && (
                                                                        <Badge className="bg-orange-500/20 text-orange-400 text-xs ml-1">
                                                                            {scanningAgents[agent.agent_id].findings} found
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 text-xs"
                                                                    onClick={() => handleStartScan(agent.agent_id)}
                                                                    disabled={commandLoading || agent.status !== 'online'}
                                                                    data-testid={`scan-btn-${agent.agent_id}`}
                                                                >
                                                                    <Play className="w-3 h-3 mr-1" /> Scan Now
                                                                </Button>
                                                            )}
                                                            
                                                            {/* View Last Scan Results */}
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 text-xs"
                                                                onClick={() => handleViewScanResults(agent.agent_id)}
                                                                data-testid={`view-scan-btn-${agent.agent_id}`}
                                                            >
                                                                <Eye className="w-3 h-3 mr-1" /> View Results
                                                            </Button>
                                                            
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 text-xs"
                                                                onClick={() => handleSendCommand(agent.agent_id, 'isolate_network', {})}
                                                                disabled={commandLoading || agent.status !== 'online'}
                                                            >
                                                                <Ban className="w-3 h-3 mr-1" /> Isolate Network
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10 text-xs"
                                                                onClick={() => {
                                                                    const pid = prompt('Enter Process ID to kill:');
                                                                    if (pid) handleSendCommand(agent.agent_id, 'kill_process', { pid: parseInt(pid) });
                                                                }}
                                                                disabled={commandLoading || agent.status !== 'online'}
                                                            >
                                                                <Square className="w-3 h-3 mr-1" /> Kill Process
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                                                                onClick={() => {
                                                                    const filePath = prompt('Enter file path to quarantine:');
                                                                    if (filePath) handleSendCommand(agent.agent_id, 'quarantine_file', { file_path: filePath });
                                                                }}
                                                                disabled={commandLoading || agent.status !== 'online'}
                                                            >
                                                                <FileX className="w-3 h-3 mr-1" /> Quarantine File
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            ) : (
                                <Card className="glass-card border-0">
                                    <CardContent className="p-12 text-center">
                                        <Cpu className="w-16 h-16 mx-auto mb-4 text-white/20" />
                                        <h3 className="text-xl font-bold mb-2">No Agents Connected</h3>
                                        <p className="text-white/60 mb-6">Deploy the Shiro agent on your devices to start monitoring</p>
                                        <Link to="/agent/download">
                                            <Button className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700">
                                                <Download className="w-4 h-4 mr-2" /> Download Agent
                                            </Button>
                                        </Link>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    )}

                    {/* Threats Tab */}
                    {activeTab === 'threats' && (
                        <div className="space-y-4">
                            {threats.map((threat) => (
                                <Card key={threat.id} className="glass-card border-0" data-testid={`threat-${threat.id}`}>
                                    <CardContent className="p-6">
                                        <div className="flex items-start gap-4">
                                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getSeverityColor(threat.severity)}`}>
                                                <AlertTriangle className="w-6 h-6" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="font-bold text-lg">{threat.title}</h3>
                                                    <Badge className={getSeverityColor(threat.severity)}>
                                                        {threat.severity}
                                                    </Badge>
                                                    <Badge variant="outline" className="text-white/60 border-white/20">
                                                        {threat.status}
                                                    </Badge>
                                                </div>
                                                <p className="text-white/60 mb-4">{threat.description}</p>
                                                <div className="flex flex-wrap gap-4 text-sm text-white/40">
                                                    {threat.source_ip && (
                                                        <div>Source IP: <span className="text-white/60 font-mono">{threat.source_ip}</span></div>
                                                    )}
                                                    {threat.mitre_attack_id && (
                                                        <div>MITRE ATT&CK: <span className="text-cyan-400">{threat.mitre_attack_id}</span></div>
                                                    )}
                                                    <div>Detected: <span className="text-white/60">{new Date(threat.detected_at).toLocaleString()}</span></div>
                                                </div>
                                            </div>
                                            {threat.status === 'investigating' && (
                                                <Button 
                                                    onClick={() => handleResolve(threat.id)}
                                                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                                    data-testid={`resolve-${threat.id}`}
                                                >
                                                    <CheckCircle className="w-4 h-4 mr-2" />
                                                    Resolve
                                                </Button>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                            {threats.length === 0 && (
                                <div className="text-center py-12 text-white/40">
                                    <Shield className="w-16 h-16 mx-auto mb-4 text-green-400" />
                                    <div className="text-xl font-medium text-white mb-2">All Clear!</div>
                                    <div>No threats detected. Your systems are protected.</div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Audit Logs Tab */}
                    {activeTab === 'audit' && (
                        <Card className="glass-card border-0">
                            <CardHeader>
                                <CardTitle>Immutable Audit Trail</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {auditLogs.map((log) => {
                                        const Icon = getAgentIcon(log.agent_layer);
                                        return (
                                            <div key={log.id} className="flex items-start gap-4 p-4 bg-white/5 rounded-lg" data-testid={`audit-${log.id}`}>
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getAgentColor(log.agent_layer)}`}>
                                                    <Icon className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-medium capitalize">{log.agent_layer}</span>
                                                        <span className="text-white/40">|</span>
                                                        <span className="text-white/60">{log.action}</span>
                                                    </div>
                                                    <p className="text-sm text-white/40">{log.reasoning}</p>
                                                    <div className="text-xs text-white/30 mt-2">
                                                        {new Date(log.timestamp).toLocaleString()} | Decision: {log.decision}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </main>

            {/* Scan Results Modal */}
            {scanResultsModal && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setScanResultsModal(null)}>
                    <div className="bg-[#0d0d14] border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="p-6 border-b border-white/10 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                    scanResultsModal.status === 'completed' 
                                        ? scanResultsModal.findings?.some(f => f.severity === 'critical' || f.severity === 'high')
                                            ? 'bg-red-500/20 text-red-400'
                                            : 'bg-green-500/20 text-green-400'
                                        : 'bg-gray-500/20 text-gray-400'
                                }`}>
                                    {scanResultsModal.status === 'completed' 
                                        ? scanResultsModal.findings?.some(f => f.severity === 'critical' || f.severity === 'high')
                                            ? <AlertTriangle className="w-6 h-6" />
                                            : <CheckCircle className="w-6 h-6" />
                                        : <RefreshCw className="w-6 h-6" />
                                    }
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">Security Scan Report</h2>
                                    <p className="text-white/60 text-sm">
                                        {scanResultsModal.started_at 
                                            ? new Date(scanResultsModal.started_at).toLocaleString() 
                                            : 'Pending'}
                                    </p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setScanResultsModal(null)}
                                className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Content - Scrollable area */}
                        <div className="p-6 overflow-y-auto flex-1">
                            {/* Summary Stats */}
                            <div className="grid grid-cols-4 gap-4 mb-6">
                                <div className="bg-white/5 rounded-lg p-4 text-center">
                                    <div className="text-2xl font-bold text-white">{scanResultsModal.total_items_scanned || 0}</div>
                                    <div className="text-xs text-white/60">Items Scanned</div>
                                </div>
                                <div className="bg-white/5 rounded-lg p-4 text-center">
                                    <div className="text-2xl font-bold text-white">{scanResultsModal.findings?.length || 0}</div>
                                    <div className="text-xs text-white/60">Total Findings</div>
                                </div>
                                <div className="bg-red-500/10 rounded-lg p-4 text-center">
                                    <div className="text-2xl font-bold text-red-400">
                                        {scanResultsModal.findings?.filter(f => f.severity === 'critical' || f.severity === 'high').length || 0}
                                    </div>
                                    <div className="text-xs text-white/60">High/Critical</div>
                                </div>
                                <div className="bg-green-500/10 rounded-lg p-4 text-center">
                                    <div className={`text-2xl font-bold ${scanResultsModal.status === 'completed' ? 'text-green-400' : 'text-yellow-400'}`}>
                                        {scanResultsModal.status === 'completed' ? '100%' : `${scanResultsModal.progress || 0}%`}
                                    </div>
                                    <div className="text-xs text-white/60">Progress</div>
                                </div>
                            </div>

                            {/* Findings List */}
                            {scanResultsModal.findings && scanResultsModal.findings.length > 0 ? (
                                <div className="space-y-3">
                                    <h3 className="text-white font-semibold mb-3">Findings</h3>
                                    {scanResultsModal.findings.map((finding, idx) => (
                                        <div key={idx} className={`p-4 rounded-lg border ${
                                            finding.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                                            finding.severity === 'high' ? 'bg-orange-500/10 border-orange-500/30' :
                                            finding.severity === 'medium' ? 'bg-yellow-500/10 border-yellow-500/30' :
                                            'bg-blue-500/10 border-blue-500/30'
                                        }`}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-start gap-3">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                                        finding.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                                                        finding.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                                                        finding.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                                        'bg-blue-500/20 text-blue-400'
                                                    }`}>
                                                        <AlertTriangle className="w-4 h-4" />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="font-medium text-white">{finding.title}</span>
                                                            <Badge className={`text-xs ${
                                                                finding.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                                                                finding.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                                                                finding.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                                                'bg-blue-500/20 text-blue-400'
                                                            }`}>
                                                                {finding.severity}
                                                            </Badge>
                                                            <Badge variant="outline" className="text-xs text-white/60 border-white/20">
                                                                {finding.category}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-sm text-white/60">{finding.description}</p>
                                                        {finding.recommendation && (
                                                            <p className="text-xs text-cyan-400 mt-2">
                                                                💡 {finding.recommendation}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
                                    <h3 className="text-xl font-bold text-white mb-2">All Clear!</h3>
                                    <p className="text-white/60">No security issues found during this scan.</p>
                                </div>
                            )}

                            {/* AI Analysis Result - Now inside scrollable area */}
                            {aiAnalysisResult && scanResultsModal && aiAnalysisResult.scan_id === scanResultsModal.id && (
                                <div className="mt-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                                    <div className="flex items-start gap-3">
                                        <Brain className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                <h4 className="font-medium text-purple-400">AI Analysis</h4>
                                                <Badge className={`text-xs ${
                                                    (aiAnalysisResult.severity_assessment || '').toLowerCase() === 'critical' ? 'bg-red-500/20 text-red-400' :
                                                    (aiAnalysisResult.severity_assessment || '').toLowerCase() === 'high' ? 'bg-orange-500/20 text-orange-400' :
                                                    (aiAnalysisResult.severity_assessment || '').toLowerCase() === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                                    'bg-green-500/20 text-green-400'
                                                }`}>
                                                    {aiAnalysisResult.severity_assessment || 'N/A'} ({aiAnalysisResult.risk_score || 0}/100)
                                                </Badge>
                                                {aiAnalysisResult.ai_powered && (
                                                    <Badge className="bg-purple-500/20 text-purple-400 text-xs">AI-Powered</Badge>
                                                )}
                                            </div>
                                            <p className="text-sm text-white/80 mb-3">{aiAnalysisResult.summary || 'No summary available.'}</p>
                                            {Array.isArray(aiAnalysisResult.recommended_actions) && aiAnalysisResult.recommended_actions.length > 0 && (
                                                <div className="mt-3 pt-3 border-t border-purple-500/20">
                                                    <p className="text-xs text-white/60 mb-2 font-medium">Recommended Actions:</p>
                                                    <ul className="space-y-2">
                                                        {aiAnalysisResult.recommended_actions.slice(0, 5).map((action, i) => (
                                                            <li key={i} className="flex items-start gap-2 text-sm text-cyan-400">
                                                                <span className="text-cyan-500 mt-0.5">•</span>
                                                                <span className="flex-1">{typeof action === 'string' ? action : (action.action || action.description || JSON.stringify(action))}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 border-t border-white/10 flex justify-between">
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline"
                                    onClick={() => handleAIAnalysis(scanResultsModal.id)}
                                    disabled={aiAnalyzing}
                                    className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                                >
                                    {aiAnalyzing ? (
                                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                                    ) : (
                                        <><Brain className="w-4 h-4 mr-2" /> AI Analysis</>
                                    )}
                                </Button>
                                {scanResultsModal.findings?.some(f => f.severity === 'high' || f.severity === 'critical') && (
                                    <Button 
                                        variant="outline"
                                        onClick={() => handleCreateThreatsFromScan(scanResultsModal.id)}
                                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                    >
                                        <AlertTriangle className="w-4 h-4 mr-2" /> Create Threats
                                    </Button>
                                )}
                            </div>
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setScanResultsModal(null);
                                    setAiAnalysisResult(null);
                                }}
                                className="border-white/20 text-white/60"
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
